/**
 * Moves the item at fromIndex to toIndex; the items in between shift by one. Indexes
 * outside the array are clamped to it.
 */
export const moveItem = <T>(
  items: T[],
  fromIndex: number,
  toIndex: number
): T[] => {
  if (fromIndex < 0 || fromIndex >= items.length) {
    return items;
  }

  const target = Math.min(Math.max(toIndex, 0), items.length - 1);
  const result = [...items];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(target, 0, moved);

  return result;
};

/**
 * Moves an item within a subset of the items (e.g. the rows of one section of a list
 * shared by several sections). The subset keeps the places it has in the whole list:
 * only the order of its items changes, the other items stay where they are.
 */
export const moveItemWithinSubset = <T>(
  items: T[],
  isInSubset: (item: T) => boolean,
  fromIndex: number,
  toIndex: number
): T[] => {
  const subset = items.filter(isInSubset);
  const reordered = moveItem(subset, fromIndex, toIndex);

  let next = 0;
  return items.map((item) => {
    if (!isInSubset(item)) {
      return item;
    }

    const replacement = reordered[next];
    next += 1;
    return replacement;
  });
};
